import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  listChats,
  getChatMessages,
  getChatSignals,
  updateChat,
  getSettings,
} from '../api/client';
import {
  ChatListItem,
  MessageBubble,
  EmptyState,
} from '../components';
import type { Chat } from '../components/ChatListItem';
import type { Message } from '../components/MessageBubble';
import ToggleSwitch from '../components/ToggleSwitch';

interface ChatSignalOverlay {
  message_id: string;
  category: string;
}

interface ChatSettings {
  priority: string;
  monitoring: boolean;
  sensitivity: number;
}

export default function Conversations() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [chats, setChats] = useState<Chat[]>([]);
  const [filteredChats, setFilteredChats] = useState<Chat[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState<'time' | 'signals'>('time');
  const [loading, setLoading] = useState(true);
  const [monitoringEnabled, setMonitoringEnabled] = useState<boolean | null>(null);

  const [activeChatId, setActiveChatId] = useState<string | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [signalOverlays, setSignalOverlays] = useState<Map<string, string>>(new Map());
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  const scrollRef = useRef<HTMLDivElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const lastScrollHeightRef = useRef<number>(0);
  const lastFirstMessageIdRef = useRef<string | null>(null);
  const shouldScrollToBottomRef = useRef<boolean>(true);

  // Settings drawer
  const [showSettings, setShowSettings] = useState(false);
  const [settings, setSettings] = useState<ChatSettings>({
    priority: 'normal',
    monitoring: true,
    sensitivity: 5,
  });
  const [savingSettings, setSavingSettings] = useState(false);

  // Mobile: show list or messages
  const [mobileView, setMobileView] = useState<'list' | 'messages'>('list');

  // Check if chat monitoring is enabled
  useEffect(() => {
    getSettings()
      .then((res) => {
        const s = res.data?.settings ?? res.data ?? {};
        setMonitoringEnabled(s.chat_monitoring_enabled !== false);
      })
      .catch(() => setMonitoringEnabled(true));
  }, []);

  // Fetch chats (only if monitoring is enabled)
  useEffect(() => {
    if (monitoringEnabled === null || monitoringEnabled === false) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    async function fetchChats() {
      try {
        const res = await listChats();
        if (cancelled) return;
        const data = res.data;
        const all: Chat[] = Array.isArray(data) ? data : data.items ?? [];
        const items = all.filter((c) => c.is_monitored !== false);
        setChats(items);
        setFilteredChats(items);
      } catch {
        /* ignore */
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    fetchChats();
    return () => { cancelled = true; };
  }, [monitoringEnabled]);
  useEffect(() => {
    let result = [...chats];

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(
        (c) =>
          c.name.toLowerCase().includes(q) ||
          c.last_message_preview?.toLowerCase().includes(q),
      );
    }

    result.sort((a, b) => {
      if (sortBy === 'signals') {
        return (b.signal_count ?? 0) - (a.signal_count ?? 0);
      }
      const ta = a.last_message_at ? new Date(a.last_message_at).getTime() : 0;
      const tb = b.last_message_at ? new Date(b.last_message_at).getTime() : 0;
      return tb - ta;
    });

    setFilteredChats(result);
  }, [chats, searchQuery, sortBy]);

  // Fetch messages when active chat changes
  const fetchChatData = useCallback(async (chatId: string) => {
    setMessagesLoading(true);
    setMessages([]);
    setSignalOverlays(new Map());
    setHasMore(true);
    shouldScrollToBottomRef.current = true;

    try {
      const [msgRes, sigRes] = await Promise.allSettled([
        getChatMessages(chatId, { limit: 50 }),
        getChatSignals(chatId),
      ]);

      if (msgRes.status === 'fulfilled') {
        const data = msgRes.value.data;
        const fetchedMessages: Message[] = Array.isArray(data) ? data : data.items ?? [];
        if (fetchedMessages.length < 50) setHasMore(false);
        
        // Sort messages by timestamp ascending
        setMessages(
          [...fetchedMessages].sort(
            (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime(),
          ),
        );
      }

      if (sigRes.status === 'fulfilled') {
        const data = sigRes.value.data;
        const sigs: ChatSignalOverlay[] = Array.isArray(data) ? data : data.items ?? [];
        const overlayMap = new Map<string, string>();
        sigs.forEach((s) => overlayMap.set(s.message_id, s.category));
        setSignalOverlays(overlayMap);
      }
    } catch {
      /* ignore */
    } finally {
      setMessagesLoading(false);
    }
  }, []);

  const loadMoreMessages = useCallback(async () => {
    if (!activeChatId || loadingMore || !hasMore) return;
    setLoadingMore(true);

    try {
      // Find oldest message timestamp
      const oldestTs = messages.length > 0 ? messages[0].timestamp : undefined;
      const res = await getChatMessages(activeChatId, { 
        limit: 50, 
        to_timestamp: oldestTs 
      });
      
      const fetched: Message[] = Array.isArray(res.data) ? res.data : res.data.items ?? [];
      if (fetched.length === 0) {
        setHasMore(false);
        return;
      }
      if (fetched.length < 50) setHasMore(false);

      const sorted = [...fetched].sort(
        (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
      );

      setMessages(prev => [...sorted, ...prev]);
    } catch {
      setHasMore(false);
    } finally {
      setLoadingMore(false);
    }
  }, [activeChatId, loadingMore, hasMore, messages]);

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop } = e.currentTarget;
    if (scrollTop < 50 && hasMore && !loadingMore) {
      loadMoreMessages();
    }
  };

  // Maintain scroll position when messages are added to the top (infinite scroll)
  // Or scroll to bottom on initial load / new message
  useEffect(() => {
    if (!scrollRef.current) return;

    if (shouldScrollToBottomRef.current) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'auto' });
      shouldScrollToBottomRef.current = false;
    } else if (lastFirstMessageIdRef.current) {
      const firstMsg = messages[0];
      if (firstMsg && (firstMsg.id || firstMsg.timestamp) !== lastFirstMessageIdRef.current) {
        const newScrollHeight = scrollRef.current.scrollHeight;
        const delta = newScrollHeight - lastScrollHeightRef.current;
        scrollRef.current.scrollTop += delta;
      }
    }

    lastScrollHeightRef.current = scrollRef.current.scrollHeight;
    const firstMsg = messages[0];
    lastFirstMessageIdRef.current = firstMsg ? (firstMsg.id || firstMsg.timestamp) : null;
  }, [messages]);

  const handleSelectChat = (chatId: string) => {
    setActiveChatId(chatId);
    setShowSettings(false);
    setMobileView('messages');

    const chat = chats.find((c) => c.id === chatId);
    if (chat) {
      setSettings({
        priority: chat.priority ?? 'normal',
        monitoring: chat.is_monitored !== false,
        sensitivity: 5,
      });
    }

    fetchChatData(chatId);
  };

  const handleSaveSettings = async () => {
    if (!activeChatId) return;
    setSavingSettings(true);
    try {
      await updateChat(activeChatId, {
        priority: settings.priority,
        is_monitored: settings.monitoring,
        sensitivity: settings.sensitivity,
      });
      setChats((prev) =>
        prev.map((c) =>
          c.id === activeChatId
            ? { ...c, priority: settings.priority as Chat['priority'] }
            : c,
        ),
      );
      setShowSettings(false);
    } catch {
      /* ignore */
    } finally {
      setSavingSettings(false);
    }
  };

  const activeChat = chats.find((c) => c.id === activeChatId);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
        <span className="ml-3 text-gray-500">{t('common.loading')}</span>
      </div>
    );
  }

  if (monitoringEnabled === false) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center space-y-4">
          <span className="text-5xl block">💬</span>
          <h2 className="text-xl font-semibold text-gray-700">{t('conversations.monitoringDisabled')}</h2>
          <p className="text-sm text-gray-500">{t('conversations.monitoringDisabledDesc')}</p>
          <button
            onClick={() => navigate('/settings')}
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 transition-colors"
          >
            ⚙️ {t('conversations.goToSettings')}
          </button>
        </div>
      </div>
    );
  }

  if (chats.length === 0) {
    return (
      <div className="space-y-6">
        <EmptyState
          icon="💬"
          title={t('conversations.title')}
          description={t('conversations.emptyState')}
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-[calc(100vh-8rem)] gap-0">

      <div className="flex flex-1 rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
      {/* Left panel: Chat list */}
      <div
        className={`w-full md:w-80 md:min-w-[320px] border-r border-gray-200 flex flex-col ${
          mobileView === 'messages' ? 'hidden md:flex' : 'flex'
        }`}
      >
        {/* Search + Sort */}
        <div className="p-3 border-b border-gray-200 space-y-2">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={t('common.search')}
            className="w-full rounded-md border border-gray-300 bg-gray-50 px-3 py-2 text-sm placeholder-gray-400 focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none"
          />
          <div className="flex gap-1">
            <button
              onClick={() => setSortBy('time')}
              className={`flex-1 rounded-md px-2 py-1 text-xs font-medium transition-colors ${
                sortBy === 'time'
                  ? 'bg-primary-100 text-primary-700'
                  : 'text-gray-500 hover:bg-gray-100'
              }`}
            >
              {t('conversations.lastMessage')}
            </button>
            <button
              onClick={() => setSortBy('signals')}
              className={`flex-1 rounded-md px-2 py-1 text-xs font-medium transition-colors ${
                sortBy === 'signals'
                  ? 'bg-primary-100 text-primary-700'
                  : 'text-gray-500 hover:bg-gray-100'
              }`}
            >
              {t('conversations.signalsDetected')}
            </button>
          </div>
        </div>

        {/* Chat list */}
        <div className="flex-1 overflow-y-auto">
          {filteredChats.length === 0 ? (
            <div className="p-4 text-center text-sm text-gray-400">
              {t('common.noResults')}
            </div>
          ) : (
            filteredChats.map((chat) => (
              <ChatListItem
                key={chat.id}
                chat={chat}
                isActive={chat.id === activeChatId}
                onClick={handleSelectChat}
              />
            ))
          )}
        </div>
      </div>

      {/* Right panel: Messages */}
      <div
        className={`flex-1 flex flex-col min-w-0 ${
          mobileView === 'list' ? 'hidden md:flex' : 'flex'
        }`}
      >
        {!activeChat ? (
          <div className="flex-1 flex items-center justify-center text-gray-400">
            <div className="text-center">
              <span className="text-4xl block mb-2">💬</span>
              <p className="text-sm">{t('conversations.subtitle')}</p>
            </div>
          </div>
        ) : (
          <>
            {/* Chat header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gray-50">
              <div className="flex items-center gap-3 min-w-0">
                {/* Mobile back button */}
                <button
                  onClick={() => setMobileView('list')}
                  className="md:hidden text-gray-500 hover:text-gray-700"
                >
                  ←
                </button>

                <span className="text-lg shrink-0">
                  {activeChat.chat_type === 'private' ? '💬' : '👥'}
                </span>
                <div className="min-w-0">
                  <h2 className="text-sm font-semibold text-gray-900 truncate">
                    {activeChat.name}
                  </h2>
                  <div className="flex items-center gap-2 text-xs text-gray-500">
                    <span>
                      {activeChat.chat_type === 'private'
                        ? t('conversations.privateChat')
                        : t('conversations.groupChat')}
                    </span>
                    <span className="inline-flex items-center gap-1">
                      <span
                        className={`inline-block h-1.5 w-1.5 rounded-full ${
                          activeChat.health === 'important'
                            ? 'bg-green-500'
                            : activeChat.health === 'active'
                            ? 'bg-blue-500'
                            : activeChat.health === 'noisy'
                            ? 'bg-yellow-500'
                            : 'bg-gray-400'
                        }`}
                      />
                      {t(`conversations.health.${activeChat.health}`)}
                    </span>
                    {activeChat.signal_count != null && activeChat.signal_count > 0 && (
                      <span className="text-primary-600 font-medium">
                        {activeChat.signal_count} {t('conversations.signalsDetected')}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              <button
                onClick={() => setShowSettings(!showSettings)}
                className="shrink-0 rounded-md p-2 text-gray-500 hover:bg-gray-200 transition-colors"
                title={t('conversations.chatSettings')}
              >
                ⚙️
              </button>
            </div>

            {/* Content area: messages + optional settings drawer */}
            <div className="flex-1 flex overflow-hidden relative">
              {/* Messages timeline */}
              <div 
                ref={scrollRef}
                onScroll={handleScroll}
                className="flex-1 overflow-y-auto p-4 space-y-3 bg-gray-50"
              >
                {messagesLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600" />
                    <span className="ml-2 text-sm text-gray-500">{t('common.loading')}</span>
                  </div>
                ) : messages.length === 0 ? (
                  <div className="flex items-center justify-center py-12 text-sm text-gray-400">
                    {t('common.noResults')}
                  </div>
                ) : (
                  <>
                    {loadingMore && (
                      <div className="flex justify-center py-2">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600" />
                      </div>
                    )}
                    {messages.map((msg) => (
                      <div key={msg.id} className="flex flex-col">
                        <MessageBubble
                          message={msg}
                          isHighlighted={signalOverlays.has(msg.id)}
                          signalBadge={signalOverlays.get(msg.id)}
                        />
                      </div>
                    ))}
                    <div ref={messagesEndRef} />
                  </>
                )}
              </div>

              {/* Settings drawer */}
              <div
                className={`absolute top-0 right-0 h-full w-72 bg-white border-l border-gray-200 shadow-lg transform transition-transform duration-300 z-10 ${
                  showSettings ? 'translate-x-0' : 'translate-x-full'
                }`}
              >
                <div className="p-4 space-y-5">
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-semibold text-gray-900">
                      {t('conversations.chatSettings')}
                    </h3>
                    <button
                      onClick={() => setShowSettings(false)}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      ✕
                    </button>
                  </div>

                  {/* Priority */}
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      {t('conversations.priority.high').replace('High Priority', 'Priority')}
                    </label>
                    <select
                      value={settings.priority}
                      onChange={(e) =>
                        setSettings((s) => ({ ...s, priority: e.target.value }))
                      }
                      className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none"
                    >
                      {['high', 'normal', 'low', 'muted', 'excluded'].map(
                        (p) => (
                          <option key={p} value={p}>
                            {t(`conversations.priority.${p}`)}
                          </option>
                        ),
                      )}
                    </select>
                  </div>

                  {/* Monitoring toggle */}
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-medium text-gray-700">
                      {t('conversations.monitoring')}
                    </span>
                    <ToggleSwitch
                      checked={settings.monitoring}
                      onChange={(val) => setSettings((s) => ({ ...s, monitoring: val }))}
                      size="sm"
                    />
                  </div>

                  {/* Sensitivity slider */}
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      {t('conversations.sensitivity')} ({settings.sensitivity})
                    </label>
                    <input
                      type="range"
                      min={1}
                      max={10}
                      value={settings.sensitivity}
                      onChange={(e) =>
                        setSettings((s) => ({
                          ...s,
                          sensitivity: Number(e.target.value),
                        }))
                      }
                      className="w-full"
                    />
                    <div className="flex justify-between text-[10px] text-gray-400">
                      <span>Low</span>
                      <span>High</span>
                    </div>
                  </div>

                  {/* Save */}
                  <button
                    onClick={handleSaveSettings}
                    disabled={savingSettings}
                    className="w-full rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50 transition-colors"
                  >
                    {savingSettings ? t('common.loading') : t('common.save')}
                  </button>
                </div>
              </div>
            </div>

            {/* Bottom info bar */}
            <div className="px-4 py-2 border-t border-gray-200 bg-gray-50 text-xs text-gray-400 text-center">
              📡 Monitoring mode — signals are extracted automatically
            </div>
          </>
        )}
      </div>
    </div>
    </div>
  );
}
